import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setSearchViewValue } from 'src/redux-api-bridge/menu/actions'
import { findSuggestionsByUserSearch, MealsSuggestion } from '../utils'
import { getSearchByValues } from 'src/redux-api-bridge/menu/selectors'
import { trackSearchRevampFilterApplied } from 'src/utils/tracking'
import { SearchInputState } from './SearchInput'
import { useFlag } from '@unleash/proxy-client-react'
import { FF_USE_SEARCH_SERVICE } from 'src/constants/feature-flags'
import { searchProducts } from 'src/api/sduiService'
import { getMealsSelector } from 'src/modules/MyPlan/selectors'

export const INPUT_ORIGIN = {
  USER_SEARCH: 'Typing',
  CLOSE: 'Close icon',
  PILL: 'Search Suggested Pill',
  LAST_SEARCHES: 'Last Searches'
}

export const useSearchInput = ({
  value,
  shouldShowSuggestions,
  delay,
  origin,
  menuDate
}: SearchInputState) => {
  const dispatch = useDispatch()
  const [debounceValue, setDebounceValue] = useState(value)
  const searchByParams = useSelector(getSearchByValues)
  const menuMeals = useSelector(getMealsSelector)
  const useBackendSearch = useFlag(FF_USE_SEARCH_SERVICE)

  useEffect(() => {
    let handler
    if (value?.length > 0 || !!origin) {
      handler = setTimeout(async () => {
        setDebounceValue(value)
        let suggestions: MealsSuggestion
        let searchSource = ''
        if (useBackendSearch && value && menuDate) {
          try {
            const backendResult = await searchProducts({
              queryText: value,
              menuDate: menuDate
            })
            const backendIds =
              backendResult.products?.map(p => p.inventoryId) ?? []

            // Match backend IDs to local menu meals, preserving order
            const idToMeal = Object.fromEntries(
              menuMeals.map(m => [m.inventoryId, m])
            )
            const matchedMeals = backendIds
              .map(id => idToMeal[id])
              .filter(Boolean)
            suggestions = {
              exactMatchMeals: matchedMeals.map(m => m.inventoryId),
              suggestedMeals: [],
              chefs: [],
              cuisines: [],
              ingredients: [],
              diets: [],
              proteins: []
            }
            searchSource = 'search service'
          } catch (e) {
            // fallback to local search
            suggestions = findSuggestionsByUserSearch(value, searchByParams)
            searchSource = 'fuse.js (fallback)'
          }
        } else {
          suggestions = findSuggestionsByUserSearch(value, searchByParams)
          searchSource = 'fuse.js'
        }
        dispatch(
          setSearchViewValue({
            searchedValue: value,
            suggestions,
            shouldShowSuggestions: shouldShowSuggestions,
            origin: origin
          })
        )
        trackSearchRevampFilterApplied(origin, value, searchSource)
      }, delay)
    }

    return () => {
      handler && clearTimeout(handler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    value,
    shouldShowSuggestions,
    delay,
    origin,
    menuDate,
    useBackendSearch,
    menuMeals
  ])
  return { debounceValue }
}
